import type { ExamPreviewClassification } from '../../domain/entities/content.entity';

/**
 * A classificação da questão no header do card: um chip por assunto, primária
 * primeiro (é a ordem em que o back já entrega).
 *
 * A **primária tem destaque visual** porque ela não é só "a primeira": é a
 * única que conta para a incidência do assunto
 * (`RebuildSubjectIncidenceUseCase` filtra `relation: PRIMARY`). Duas
 * classificações desenhadas iguais fariam parecer que trocar a ordem é
 * cosmético.
 *
 * Questão sem classificação mostra um aviso discreto em vez de nada: numa prova
 * recém-importada é justamente a ausência que a pessoa precisa achar, e um card
 * sem chip nenhum é indistinguível de um card cuja classificação ainda não
 * carregou.
 */
export function QuestionClassificationChips({
  classifications,
  /** Ausente = sem permissão de escrita; os chips ficam só como leitura. */
  onClick,
}: {
  classifications: ExamPreviewClassification[];
  onClick?: () => void;
}) {
  if (classifications.length === 0) {
    if (!onClick) {
      return (
        <span className="text-(--color-text-secondary) text-xs italic">
          sem classificação
        </span>
      );
    }
    return (
      <button
        type="button"
        onClick={onClick}
        title="Classificar esta questão"
        className="text-(--color-text-secondary) hover:text-(--color-accent) text-xs italic underline decoration-dotted"
      >
        sem classificação
      </button>
    );
  }

  const chips = classifications.map((entry) => (
    <span
      key={entry.subjectId}
      title={
        entry.subjectName === null
          ? `Assunto removido do cadastro (${entry.subjectId})`
          : entry.relation === 'PRIMARY'
            ? `${entry.subjectName} — classificação primária`
            : `${entry.subjectName} — classificação secundária`
      }
      className={
        entry.relation === 'PRIMARY'
          ? 'rounded bg-sky-100 px-1.5 py-0.5 text-xs font-medium text-sky-800 dark:bg-sky-500/15 dark:text-sky-300'
          : 'border-(--color-border) text-(--color-text-secondary) rounded border px-1.5 py-0.5 text-xs'
      }
    >
      {entry.subjectName ?? 'assunto removido'}
    </span>
  ));

  if (!onClick) return <div className="flex flex-wrap gap-1">{chips}</div>;

  return (
    <button
      type="button"
      onClick={onClick}
      title="Reclassificar esta questão"
      className="flex flex-wrap gap-1"
    >
      {chips}
    </button>
  );
}
